import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const WeatherContainer = styled.div`
  background: linear-gradient(135deg, #74b9ff 0%, #0984e3 100%);
  border-radius: 12px;
  padding: 15px;
  margin: 16px 20px;
  color: white;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 2px 8px rgba(116, 185, 255, 0.3);
`;

const WeatherLeft = styled.div`
  display: flex;
  flex-direction: column;
`;

const Temperature = styled.div`
  font-size: 20px;
  font-weight: bold;
  margin-bottom: 4px;
`;

const Description = styled.div`
  font-size: 14px;
  opacity: 0.9;
  text-transform: capitalize;
`;

const Advice = styled.div`
  font-size: 12px;
  margin-top: 8px;
  padding: 6px 10px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 15px;
  display: inline-block;
`;

const WeatherIcon = styled.img`
  width: 60px;
  height: 60px;
  filter: drop-shadow(0 2px 4px rgba(0,0,0,0.1));
`;

const LoadingWeather = styled.div`
  background: #f8f9fa;
  border-radius: 12px;
  padding: 15px;
  margin: 16px 20px;
  text-align: center;
  color: #6c757d;
  font-size: 14px;
`;

const getEventWeather = async (lat, lon, eventDate) => {
  try {
    // Calcola i giorni di differenza dall'oggi
    const today = new Date();
    const eventDay = new Date(eventDate);
    const diffDays = Math.ceil((eventDay - today) / (1000 * 60 * 60 * 24));
    
    // Se l'evento è passato, non mostrare meteo
    if (diffDays < 0) return null;
    
    // API con previsioni fino a 7 giorni
    const response = await fetch(
      `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&daily=temperature_2m_max,temperature_2m_min,weather_code,precipitation_sum&timezone=Europe/Rome&forecast_days=7`
    );
    
    const data = await response.json();
    
    // Se l'evento è troppo lontano (oltre 7 giorni)
    if (diffDays >= 7) return null;
    
    // Prendi i dati del giorno giusto
    const dayIndex = diffDays === 0 ? 0 : diffDays;
    const dayWeather = {
      maxTemp: data.daily.temperature_2m_max[dayIndex],
      minTemp: data.daily.temperature_2m_min[dayIndex],
      weatherCode: data.daily.weather_code[dayIndex],
      rain: data.daily.precipitation_sum[dayIndex]
    };
    
    const getWeatherDescription = (code) => {
      if (code === 0) return 'sereno';
      if (code <= 3) return 'parzialmente nuvoloso';
      if (code <= 48) return 'nebbioso';
      if (code <= 67) return 'pioggia';
      if (code <= 77) return 'neve';
      if (code <= 82) return 'acquazzoni';
      if (code <= 99) return 'temporale';
      return 'variabile';
    };
    
    const getWeatherIcon = (code) => {
      if (code === 0) return '01d';
      if (code <= 3) return '02d';
      if (code <= 48) return '50d';
      if (code <= 67) return '10d';
      if (code <= 77) return '13d';
      if (code <= 82) return '09d';
      if (code <= 99) return '11d';
      return '03d';
    };
    
    return {
      temperature: Math.round((dayWeather.maxTemp + dayWeather.minTemp) / 2),
      maxTemp: Math.round(dayWeather.maxTemp),
      minTemp: Math.round(dayWeather.minTemp),
      description: getWeatherDescription(dayWeather.weatherCode),
      icon: getWeatherIcon(dayWeather.weatherCode),
      rain: dayWeather.rain,
      isToday: diffDays === 0
    };
    
  } catch (error) {
    console.error('Errore nel recupero meteo:', error);
    return null;
  }
};


const WeatherInfo = ({ event }) => {
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchWeather = async () => {
      if (event?.latitude && event?.longitude && event?.startDate) {
        try {
          const weatherData = await getEventWeather(
            event.latitude, 
            event.longitude, 
            event.startDate
          );
          setWeather(weatherData);
        } catch (error) {
          console.error('Errore nel recupero meteo:', error);
        }
      }
      setLoading(false);
    };

    fetchWeather();
  }, [event]);

  const getWeatherAdvice = (weather) => {
    if (!weather) return '';
    
    const temp = weather.maxTemp || weather.temperature;
    
    if (weather.rain > 5) return "☔ Prevista pioggia, porta l'ombrello!";
    if (weather.rain > 0) return "🌦️ Possibili piogge leggere";
    if (temp < 8) return "🧥 Sarà freddo, vestiti pesante!";
    if (temp > 30) return "🥵 Sarà molto caldo, porta acqua!";
    if (temp > 25) return "☀️ Perfetto per stare all'aperto!";
    if (weather.isToday) return "🌤️ Condizioni attuali";
    return "🌤️ Buone condizioni previste";
    };


  if (loading) {
    return (
      <LoadingWeather>
        🌤️ Caricamento previsioni meteo...
      </LoadingWeather>
    );
  }

  if (!weather) return null;

  return (
    <WeatherContainer>
      <WeatherLeft>
        <Temperature>
        {weather.isToday ? 
            `${weather.temperature}°C` : 
            `${weather.minTemp}°-${weather.maxTemp}°C`
                }
        </Temperature>  
        <Description>{weather.description}</Description>
        <Advice>{getWeatherAdvice(weather)}</Advice>
      </WeatherLeft>
      
      <WeatherIcon
        src={`https://openweathermap.org/img/wn/${weather.icon}@2x.png`}
        alt={weather.description}
      />
    </WeatherContainer>
  );
};

export default WeatherInfo;
